import { groupAllergyRecords } from '@liza/fhir-utils';
import type { WithId } from '@medplum/core';
import type { AllergyIntolerance } from '@medplum/fhirtypes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AllergyGroupRow, type AllergyGroupRowProps } from './AllergyGroupRow';

export type AllergiesListProps = Omit<AllergyGroupRowProps, 'group'> & {
  /** The patient's whole allergy list. Grouping a partial list gives wrong reactions and tiers. */
  allergies: WithId<AllergyIntolerance>[];
};

/**
 * One row per allergen rather than one per resource, highest severity first. Rows that several
 * source records collapsed into expand to those records; a "no known allergies" row is shown only
 * as the empty state. Grouping and ordering come from `groupAllergyRecords`, which leaves out rows
 * with no usable name.
 */
export const AllergiesList = ({ allergies, ...rowProps }: AllergiesListProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const { groups, noKnownAllergies } = useMemo(() => groupAllergyRecords(allergies), [allergies]);

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="space-y-1">
        {groups.length > 0 ? (
          groups.map((group) => <AllergyGroupRow key={group.key} group={group} {...rowProps} />)
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {noKnownAllergies ? t('noKnownAllergies') : t('noAllergiesFound')}
          </p>
        )}
      </div>
    </div>
  );
};
